class Modals {
  constructor() {
    // Existing modal system
    this.modalTriggers = document.querySelectorAll('.modal-link');
    this.dismissButtons = document.querySelectorAll('.js-dismiss-modal');
    this.modals = document.querySelectorAll('.modal');

    // Image popup system (new)
    this.imageTriggers = document.querySelectorAll('.popup-trigger');

    // Build a reusable overlay for image popups
    this.imageOverlay = this.#buildImageOverlay();

    // Track last focused element for accessibility (optional)
    this._lastActive = null;
  }

  /**
   * Initialize modal open and close event listeners + image popups
   */
  init() {
    // ====== Classic modals (your existing pattern) ======
    this.modalTriggers.forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const id = el.getAttribute('data-modal');
        if (!id) return;
        this._lastActive = document.activeElement;
        this.openModalById(id);
      });
    });

    this.dismissButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeAllModals();
      });
    });

    // Optional: click outside (backdrop) to close .modal if you add data-backdrop on the modal container
    this.modals.forEach((modalEl) => {
      modalEl.addEventListener('click', (e) => {
        const isBackdrop = e.target === modalEl || e.target.hasAttribute('data-backdrop');
        if (isBackdrop) this.closeAllModals();
      });
    });

    // ====== Image popup (new) ======
    this.imageTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        // Allow inner links/buttons to behave normally if they have their own href/button
        const interactive = e.target.closest('a, button');
        if (interactive) return;

        const img = trigger.querySelector('img');
        if (!img) return;

        const fullSrc =
          trigger.getAttribute('data-full') ||
          img.getAttribute('data-full') ||
          img.currentSrc ||
          img.src;

        const alt = img.getAttribute('alt') || '';
        this._lastActive = document.activeElement;
        this.openImagePopup(fullSrc, alt);
      });
    });

    // Global ESC to close (modals or image popup)
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        // Close image popup first if open, otherwise close modals
        if (this.isImageOpen()) {
          this.closeImagePopup();
        } else {
          this.closeAllModals();
        }
      }
    });
  }

  /**
   * Open a classic modal by ID (sets display:block)
   */
  openModalById(id) {
    const el = document.getElementById(id);
    if (!el) return;
    el.style.display = 'block';
    el.setAttribute('aria-hidden', 'false');
    el.classList.add('is-open');
    // Focus the first focusable element if present
    const focusable = el.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable) focusable.focus({ preventScroll: true });
  }

  /**
   * Close all classic modals
   */
  closeAllModals() {
    this.modals.forEach((el) => {
      el.style.display = 'none';
      el.setAttribute('aria-hidden', 'true');
      el.classList.remove('is-open');
    });
    // Restore focus
    if (this._lastActive && document.contains(this._lastActive)) {
      this._lastActive.focus({ preventScroll: true });
    }
  }

  /**
   * Image popup API
   */
  openImagePopup(src, alt = '') {
    const img = this.imageOverlay.querySelector('img');
    img.src = src;
    img.alt = alt;
    this.imageOverlay.classList.add('active');
    this.imageOverlay.removeAttribute('aria-hidden');
    // Focus overlay for ESC handling when using screen readers
    this.imageOverlay.focus({ preventScroll: true });
  }

  closeImagePopup() {
    const img = this.imageOverlay.querySelector('img');
    img.removeAttribute('src');
    this.imageOverlay.classList.remove('active');
    this.imageOverlay.setAttribute('aria-hidden', 'true');
    if (this._lastActive && document.contains(this._lastActive)) {
      this._lastActive.focus({ preventScroll: true });
    }
  }

  isImageOpen() {
    return this.imageOverlay.classList.contains('active');
  }

  
  events() {
    // To be implemented if needed
  }

  /**
   * Private helpers
   */
  #buildImageOverlay() {
    // If it already exists in DOM, reuse it
    let overlay = document.getElementById('img-popup-overlay');
    if (overlay) return overlay;

    overlay = document.createElement('div');
    overlay.id = 'img-popup-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('tabindex', '-1');

    const img = document.createElement('img');
    overlay.appendChild(img);

    // Click anywhere to close
    overlay.addEventListener('click', () => this.closeImagePopup());

    document.body.appendChild(overlay);
    return overlay;
  }
}

export default Modals;
